/**
 * Overrides of a flowing text's continuation boxes (SP-154): a continuation may be moved and resized from its
 * default spot. Pure helpers: reading, clamping and storing an override, and the live preview of one being dragged.
 */
import { pageDimensions } from './paper';
import type { PageSetup } from './paper';
import { MAX_FLOW_OFFSET, MIN_ELEMENT_WIDTH } from './types';
import type { FlowOverride, PatternDocument, TextPatternElement } from './types';
import { TEXT_BOX_PADDING } from '../text-flow/plan-flow';

/** The room a moved continuation keeps below its first line, so the text always lands on the page. */
export const MIN_LANDING_ROOM = 24;

/** Continuation `segment` (>= 1) of a text: its override, or null. */
export function overrideOf(element: TextPatternElement, segment: number): FlowOverride | null {
    return element.flowOverrides?.[segment - 1] ?? null;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * Clamps an override: `dy` to [-mt, H - mt - mb - 2*PAD - MIN_LANDING_ROOM], `width` to
 * [MIN_ELEMENT_WIDTH, MAX_FLOW_OFFSET], `dx` to +-MAX_FLOW_OFFSET; non-finite fields are dropped.
 * Returns null when no field is left that differs from the default (dx, dy of 0; width equal to `boxWidth`).
 */
export function clampFlowOverride(override: FlowOverride, setup: PageSetup, boxWidth: number): FlowOverride | null {
    const { top, bottom } = setup.margins;
    const maxDy = pageDimensions(setup).height - top - bottom - 2 * TEXT_BOX_PADDING - MIN_LANDING_ROOM;
    const result: FlowOverride = {};
    if (Number.isFinite(override.dx)) {
        const dx = clamp(override.dx as number, -MAX_FLOW_OFFSET, MAX_FLOW_OFFSET);
        if (dx !== 0) result.dx = dx;
    }
    if (Number.isFinite(override.dy)) {
        const dy = clamp(override.dy as number, -top, maxDy);
        if (dy !== 0) result.dy = dy;
    }
    if (Number.isFinite(override.width)) {
        const width = clamp(override.width as number, MIN_ELEMENT_WIDTH, MAX_FLOW_OFFSET);
        if (width !== boxWidth) result.width = width;
    }
    return Object.keys(result).length === 0 ? null : result;
}

/** The list with entry `segment - 1` set to `override` (null clears it), trailing nulls trimmed; undefined when empty. */
export function withFlowOverride(
    list: ReadonlyArray<FlowOverride | null> | undefined,
    segment: number,
    override: FlowOverride | null,
): Array<FlowOverride | null> | undefined {
    const next: Array<FlowOverride | null> = [...(list ?? [])];
    while (next.length < segment) next.push(null);
    next[segment - 1] = override;
    while (next.length > 0 && next[next.length - 1] === null) next.pop();
    return next.length === 0 ? undefined : next;
}

/** A continuation override being dragged or resized, not stored yet (live preview, spec section 6.7). */
export type FlowPreview = { id: string; segment: number; override: FlowOverride | null };

const sameOverride = (a: FlowOverride | null, b: FlowOverride | null) =>
    a === b || (a !== null && b !== null && a.dx === b.dx && a.dy === b.dy && a.width === b.width);

/**
 * `document` with the preview's override in place of the stored one (clamped). Returns `document` itself when
 * `preview` is null or changes nothing.
 */
export function withPreviewOverride(document: PatternDocument, preview: FlowPreview | null): PatternDocument {
    if (preview === null) return document;
    for (let p = 0; p < document.pages.length; p++) {
        const page = document.pages[p];
        const e = page.elements.findIndex((element) => element.id === preview.id);
        if (e < 0) continue;
        const element = page.elements[e];
        if (element.type !== 'text') return document;
        const clamped =
            preview.override === null ? null : clampFlowOverride(preview.override, document.setup, element.width);
        if (sameOverride(overrideOf(element, preview.segment), clamped)) return document;
        const flowOverrides = withFlowOverride(element.flowOverrides, preview.segment, clamped);
        const { flowOverrides: _stored, ...rest } = element;
        const changed: TextPatternElement = flowOverrides === undefined ? rest : { ...rest, flowOverrides };
        const elements = page.elements.map((el, i) => (i === e ? changed : el));
        return { ...document, pages: document.pages.map((pg, i) => (i === p ? { ...page, elements } : pg)) };
    }
    return document;
}
